import Button from 'components/home/common/Button'
import { useFormContext } from 'react-hook-form'
import NavbarPopover from 'components/home/navbar/popovers/components/NavbarPopover'
import classNames from 'classnames'
import Form from 'components/form/Home'
import NavbarButton from '../../common/NavbarButton'
import { FilterSearchBarIcon } from 'components/icons'
import { useQuery } from 'urql'
import { graphql } from 'gql'
import ControlledCheckboxGroup from 'components/form/Home/ControlledCheckboxGroup'
import Paragraphs from 'components/home/common/typography/Paragraphs'
import { DashboardSearchFormFields } from 'data/types/home/search'
import { getServiceCategoryLabel } from 'data/helpers/search'
import { useMemo } from 'react'

interface ServiceCategorySelectionPopoverProps {
  className?: {
    container?: string
    button?: string
    panel?: string
  }
}

const ServiceCategorySelectionPopover = ({
  className,
}: ServiceCategorySelectionPopoverProps) => {
  const { control, watch, formState: { errors }, clearErrors, reset } = useFormContext<DashboardSearchFormFields>()

  const hasFilters = useMemo(() => watch('categories')?.length > 0 || watch('subCategories')?.length > 0 || watch('serviceFilters')?.length > 0, [
    watch('categories')?.length,
    watch('subCategories')?.length,
    watch('serviceFilters')?.length,
  ])

  const clearFilters = () => {
    reset({
      categories: [],
      subCategories: [],
      serviceFilters: [],
    })
    clearErrors()
  }

  const [{ data: categories, fetching: categoriesFetching }] = useQuery({
    query: graphql(`
      query ServiceCategoriesSelectionPopover {
        serviceCategories(first: 100) {
          data {
            id
            title
          }
        }
      }
    `),
  })

  const [{ data: subCategories, fetching: subCategoriesFetching }] = useQuery({
    query: graphql(`
      query ServiceSubCategories {
        serviceSubCategories(first: 100) {
          data {
            id
            title
          }
        }
      }
    `),
  })

  const [{ data: serviceFilters }] = useQuery({
    query: graphql(`
      query GeneralServiceFilters {
        serviceFilters(first: 100) {
          data {
            id
            value
            children {
              id
              value
            }
          }
        }
      }
    `),
  })

  return (
    <NavbarPopover
      trigger={
        <NavbarButton
          prefix={<FilterSearchBarIcon />}
          className={{
            container: 'min-w-[176px]',
          }}
          active={hasFilters}
        >
          {hasFilters
            ? (getServiceCategoryLabel(
              watch('categories'),
              watch('subCategories'),
              watch('serviceFilters'),
              {
                base: 'filter',
                singularEnding: '',
                pluralEnding: 's',
                suffix: '',
              },
            ))
            : 'Filters'
          }
        </NavbarButton>
      }
      variant="fixed"
      className={{
        panel: '!pb-0 !pt-40 !top-[calc(100%+2px)]',
        button: className?.button,
      }}
    >
      {({ close }) => (
        <>
          <div className="flex flex-col flex-1 lg:grid lg:grid-cols-2">
            <div className="flex flex-col flex-1 lg:h-max lg:border-r-2 lg:border-grey-100">
              <Form.ControlledPillSelect
                control={control}
                className={{
                  title: 'text-blue-1000',
                  container: 'px-24 lg:px-40',
                }}
                data={categories?.serviceCategories.data.map((category) => ({
                  id: category.id,
                  value: category.title,
                })) ?? []}
                fetching={categoriesFetching}
                name="categories"
                title="Category"
              />

              <Divider className="mx-24 my-20" />

              <Form.ControlledPillSelect
                control={control}
                className={{
                  container: 'px-24 lg:px-40',
                  title: 'text-blue-1000',
                }}
                data={subCategories?.serviceSubCategories.data.map((subCategory) => ({
                  id: subCategory.id,
                  value: subCategory.title,
                })) ?? []}
                fetching={subCategoriesFetching}
                name="subCategories"
                title="Sub Category"
              />
            </div>
            <div className="flex flex-col w-full px-24 gap-15 lg:px-40">
              <Paragraphs.LG Tag="span" className="my-3">Filters</Paragraphs.LG>

              <ControlledCheckboxGroup
                className={{
                  container: '!max-h-[530px] overflow-y-auto !ml-0',
                  options: { title: '!text-blue-1000' },
                }}
                control={control}
                name="serviceFilters"
                options={serviceFilters?.serviceFilters.data?.filter((tag) => tag.children.length > 0).map((tag) => ({
                  title: tag.value,
                  items: tag.children.map((child) => ({
                    value: child.id,
                    label: child.value,
                  })),
                })) ?? []}
              />
            </div>
          </div>

          <div className={classNames(
            'sticky bottom-0 z-10 flex flex-col justify-between w-full px-24 bg-white pt-10 pb-10 gap-y-10 lg:p-40 lg:flex-row-reverse',
          )}>
            <Button
              style="pill"
              variant="primary"
              type="button"
              onClick={() => {
                if (!errors?.filters) {
                  close()
                }
              }}
            >
              Apply Selection
            </Button>

            <Button
              style="pill"
              variant="grey"
              type="button"
              onClick={clearFilters}
            >
              Clear selection
            </Button>
          </div>
        </>
      )}
    </NavbarPopover>
  )
}

const Divider = ({ className }: { className?: string }) => {
  return <hr className={classNames('border-grey-200 opacity-40', className)} />
}

export default ServiceCategorySelectionPopover
